import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

// Icons
import DoneAllIcon from "@mui/icons-material/DoneAll";

export const FinalizarInterManual = () => {
  const toast = useToast();
  const { auth } = useAuth();

  const esAdmin = auth?.rol === "admin";
  const esGerente = auth?.rol === "gerente";

  const [ticajes, setTicajes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(true);

  const canAbort = typeof AbortController !== "undefined";
  const abortRef = useRef(null);

  const [modalFinalizar, setModalFinalizar] = useState(false);
  const [ticajeSeleccionado, setTicajeSeleccionado] = useState(null);

  const [finManual, setFinManual] = useState("");
  const [motivoManual, setMotivoManual] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const parseDateSafe = (value) => {
    if (!value) return null;
    const normalized =
      typeof value === "string" && value.includes(" ") && !value.includes("T")
        ? value.replace(" ", "T")
        : value;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const fmtDate = (value) => {
    const d = parseDateSafe(value);
    if (!d) return "-";
    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const toDatetimeLocalString = (dateValue) => {
    if (!dateValue) return "";
    const d = parseDateSafe(dateValue);
    if (!d) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const getInicio = (t) => t?.inicio ?? t?.fecha_inicio ?? t?.created_at ?? null;

  const fetchJSON = async (path, signal) => {
    const url = `${Global.url}${path}${path.includes("?") ? "&" : "?"}t=${Date.now()}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      cache: "no-store",
      signal,
    });

    if (res.status === 204) return null;
    if (!res.ok) throw new Error(`HTTP_${res.status}`);

    try {
      return await res.json();
    } catch {
      return null;
    }
  };

  const cargarDatos = useCallback(async () => {
    if (!auth?.id) return;

    setLoading(true);
    try {
      if (canAbort && abortRef.current) abortRef.current.abort();
      const ac = canAbort ? new AbortController() : null;
      abortRef.current = ac;

      if (esAdmin) {
        const [dataT, dataU, dataC, dataCent, dataOts] = await Promise.all([
          fetchJSON("ticajes/proceso", ac?.signal),
          fetchJSON("usuarios", ac?.signal).catch(() => []),
          fetchJSON("clientes", ac?.signal).catch(() => []),
          fetchJSON("centros/todos", ac?.signal).catch(() => []),
          fetchJSON("ordenes", ac?.signal).catch(() => []),
        ]);

        setTicajes(Array.isArray(dataT) ? dataT : dataT ? [dataT] : []);
        setUsuarios(Array.isArray(dataU) ? dataU : []);
        setClientes(Array.isArray(dataC) ? dataC : []);
        setCentros(Array.isArray(dataCent) ? dataCent : []);
        setOrdenes(Array.isArray(dataOts) ? dataOts : []);
        return;
      }

      if (esGerente) {
        const [dataT, dataU, dataC, dataCent, dataOts] = await Promise.all([
          fetchJSON("ticajes/proceso", ac?.signal),
          fetchJSON("usuarios", ac?.signal).catch(() => []),
          fetchJSON("clientes", ac?.signal).catch(() => []),
          fetchJSON("centros/todos", ac?.signal).catch(() => []),
          fetchJSON("ordenes", ac?.signal).catch(() => []),
        ]);

        const ticajesAll = Array.isArray(dataT) ? dataT : dataT ? [dataT] : [];
        const usuariosAll = Array.isArray(dataU) ? dataU : [];

        const idsEquipo = new Set(
          usuariosAll
            .filter((u) => String(u.gerente_id) === String(auth.id))
            .map((u) => String(u.id))
        );

        const ticajesEquipo = ticajesAll.filter((t) => idsEquipo.has(String(t.usuario_id)));

        setTicajes(ticajesEquipo);
        setUsuarios(usuariosAll);
        setClientes(Array.isArray(dataC) ? dataC : []);
        setCentros(Array.isArray(dataCent) ? dataCent : []);
        setOrdenes(Array.isArray(dataOts) ? dataOts : []);
        return;
      }

      setTicajes([]);
      setUsuarios([]);
      setClientes([]);
      setCentros([]);
      setOrdenes([]);
    } catch (error) {
      console.error("Error cargando datos:", error);
      toast.error("No se pudieron obtener las intervenciones en proceso.");
      setTicajes([]);
      setUsuarios([]);
      setClientes([]);
      setCentros([]);
      setOrdenes([]);
    } finally {
      setLoading(false);
    }
  }, [auth?.id, auth?.id, esAdmin, esGerente, toast]);

  useEffect(() => {
    cargarDatos();

    return () => {
      if (canAbort && abortRef.current) abortRef.current.abort();
    };
  }, [cargarDatos]);

  const mapUsuario = useMemo(() => {
    return Object.fromEntries(
      (usuarios || []).map((u) => [
        String(u.id),
        [u.nombre, u.apellidos].filter(Boolean).join(" ").trim() || u.nombre || `ID ${u.id}`,
      ])
    );
  }, [usuarios]);

  const mapCliente = useMemo(() => {
    return Object.fromEntries((clientes || []).map((c) => [String(c.id), c.nombre || `Cliente ${c.id}`]));
  }, [clientes]);

  const mapCentro = useMemo(() => {
    return Object.fromEntries((centros || []).map((c) => [String(c.id), c.nombre || `Centro ${c.id}`]));
  }, [centros]);

  const mapOrden = useMemo(() => {
    return Object.fromEntries(
      (ordenes || []).map((o) => [String(o.id), o.titulo || o.nombre || `OT ${o.id}`])
    );
  }, [ordenes]);

  const getNombreUsuario = (t) => {
    const uid = String(t?.usuario_id ?? t?.user_id ?? t?.usuario?.id ?? "");
    return mapUsuario[uid] || t?.usuario || (uid ? `ID ${uid}` : "-");
  };

  const ticajesOrdenados = useMemo(() => {
    return [...(ticajes || [])].sort((a, b) => {
      const da = parseDateSafe(getInicio(a)) || new Date(0);
      const db = parseDateSafe(getInicio(b)) || new Date(0);
      return db - da;
    });
  }, [ticajes]);

  const abrirFinalizar = (t) => {
    setTicajeSeleccionado(t);
    setFinManual(toDatetimeLocalString(new Date()));
    setMotivoManual("");
    setFormError("");
    setModalFinalizar(true);
  };

  const cerrarFinalizar = () => {
    if (saving) return;
    setModalFinalizar(false);
    setTicajeSeleccionado(null);
    setFinManual("");
    setMotivoManual("");
    setFormError("");
  };

  const finalizarIntervencion = async () => {
    if (!ticajeSeleccionado?.id) return;

    if (!finManual || !motivoManual.trim()) {
      setFormError("Debes indicar la fecha/hora de fin y un motivo.");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${Global.url}ticajes/finalizar-manualmente?t=${Date.now()}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        cache: "no-store",
        body: JSON.stringify({
          id: ticajeSeleccionado.id,
          fin: finManual,
          observaciones_admin: motivoManual.trim(),
        }),
      });

      let result = null;
      try {
        result = await res.json();
      } catch {}

      if (!res.ok) {
        toast.error((result && result.message) || `Error al finalizar (HTTP ${res.status})`);
        return;
      }

      toast.success("Intervención finalizada correctamente.");
      cerrarFinalizar();
      await cargarDatos();
    } catch (error) {
      console.error("Error al finalizar:", error);
      toast.error("Error de conexión.");
    } finally {
      setSaving(false);
    }
  };

  const rows = useMemo(
    () =>
      ticajesOrdenados.map((t) => {
        const inicio = getInicio(t);
        return {
          ...t,
          usuario_nombre: getNombreUsuario(t),
          cliente_nombre: mapCliente[String(t.cliente_id)] || t.cliente || t.cliente_nombre || `ID ${t.cliente_id ?? "-"}`,
          centro_nombre: mapCentro[String(t.centro_id)] || t.centro || t.centro_nombre || `ID ${t.centro_id ?? "-"}`,
          orden_nombre: mapOrden[String(t.orden_id)] || t.orden || t.orden_nombre || (t.orden_id ? `OT ${t.orden_id}` : "-"),
          inicio_txt: fmtDate(inicio),
          comentarios_txt: t.comentarios?.trim() ? t.comentarios : "Sin comentarios",
        };
      }),
    [ticajesOrdenados, mapUsuario, mapCliente, mapCentro, mapOrden]
  );

  const columns = useMemo(
    () => [
      { field: "usuario_nombre", headerName: "Usuario", flex: 1, minWidth: 220 },
      { field: "cliente_nombre", headerName: "Cliente", flex: 1, minWidth: 220 },
      { field: "centro_nombre", headerName: "Centro", flex: 1, minWidth: 220 },
      { field: "orden_nombre", headerName: "OT", flex: 1, minWidth: 240 },
      { field: "inicio_txt", headerName: "Inicio", minWidth: 180 },
      { field: "comentarios_txt", headerName: "Comentarios", flex: 1, minWidth: 260 },
      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 180,
        sortable: false,
        renderCell: (params) => {
          const t = params.row;
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
              <Button size="small" startIcon={<DoneAllIcon />} onClick={() => abrirFinalizar(t)}>
                Finalizar
              </Button>
            </Box>
          );
        },
      },
    ],
    []
  );

  const titulo = esAdmin
    ? "Finalizar Intervenciones en Proceso"
    : "Finalizar Intervenciones en Proceso de mi equipo";

  const textoSinFilas = esAdmin
    ? "No hay intervenciones activas actualmente."
    : "No hay intervenciones activas de tu equipo.";

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>

        <Paper sx={{ height: 640, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            loading={loading}
            pagination
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                  {loading ? "Cargando…" : textoSinFilas}
                </Box>
              ),
            }}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              },
            }}
          />
        </Paper>

        <Dialog open={modalFinalizar} onClose={cerrarFinalizar} fullWidth maxWidth="sm">
          <DialogTitle>Finalizar intervención</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {ticajeSeleccionado
                  ? `${getNombreUsuario(ticajeSeleccionado)} · ${mapCentro[String(ticajeSeleccionado.centro_id)] || ticajeSeleccionado.centro || ticajeSeleccionado.centro_nombre || ""} · Inicio: ${fmtDate(getInicio(ticajeSeleccionado))}`
                  : ""}
              </Typography>

              <TextField
                label="Fecha/hora fin"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={finManual}
                onChange={(e) => setFinManual(e.target.value)}
              />

              <TextField
                label="Motivo / observaciones"
                value={motivoManual}
                onChange={(e) => setMotivoManual(e.target.value)}
                placeholder="Motivo del cierre manual"
                multiline
                minRows={3}
              />

              {formError && <Typography color="error">{formError}</Typography>}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={cerrarFinalizar} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={finalizarIntervencion} disabled={saving}>
              {saving ? "Finalizando..." : "Finalizar"}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};